/* eslint-disable max-lines */
import { Icon24DownloadOutline } from '@vkontakte/icons';
import { Spacing } from '@vkontakte/vkui';
import classNames from 'clsx';
import vkTechDarkImg from 'img/vktech/logo-dark.png';
import vkTechLightImg from 'img/vktech/logo-light.png';
import React, { type ReactElement, memo, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import { Link as RouterLink, useLocation } from 'react-router-dom';
import {
    BLOCKED_B2B_OVERQUOTA,
    DOWNLOAD_DISK_O_PROMO_CONFIG,
    ENABLE_FULL_RESPONSIVE,
    IS_BLOCKED,
    IS_DOCUMENTS_DOMAIN,
    NEW_PORTAL_HEADER,
    SHOW_BLOCKED,
} from 'reactApp/appHelpers/configHelpers';
import { abSafeFakeDoorSelector, isAllDocumentsHelpButton } from 'reactApp/appHelpers/featuresHelpers';
import { aiPhotoGenerationEnabled } from 'reactApp/appHelpers/featuresHelpers/features/aiPhotoGeneration';
import { isNeuroTariffPromoObject } from 'reactApp/appHelpers/featuresHelpers/features/aiTariff';
import { boardUrl } from 'reactApp/appHelpers/featuresHelpers/features/boardEntryPoints';
import { familyAddButton } from 'reactApp/appHelpers/featuresHelpers/features/familyFunctions';
import { isFreemiumOnboardBannerEnable } from 'reactApp/appHelpers/featuresHelpers/features/freemium';
import { isHomeSidebar } from 'reactApp/appHelpers/featuresHelpers/features/homeSidebarAd';
import { IS_SPACE_POSITION_BOTTOM } from 'reactApp/appHelpers/featuresHelpers/features/spacePosition';
import { UNIFIED_SEARCH_IN_SUPERAPP_ENABLED } from 'reactApp/appHelpers/featuresHelpers/features/unifiedSearchInSuperApp';
import { forceVigboLinkedAccount } from 'reactApp/appHelpers/featuresHelpers/features/vigbo';
import { chooseVariant } from 'reactApp/appHelpers/featuresHelpers/utils';
import { HelpButton } from 'reactApp/components/HelpButton/HelpButton';
import { BREAKPOINT_MD } from 'reactApp/constants/breakpoints';
import { useMinWidthBreakpoint } from 'reactApp/hooks/responsiveness/useMinWidthBreakpoint';
import { useResponsiveEnabled } from 'reactApp/hooks/responsiveness/useResponsiveEnabled';
import { useIsWindowWidthSmall } from 'reactApp/hooks/useNormalizedOvidius/useIsWindowWidthSmall';
import { usePersonalizationInit } from 'reactApp/hooks/usePersonalizationInit';
import { usePortal } from 'reactApp/hooks/usePortal';
import { ActionPanelSelectors } from 'reactApp/modules/actionpanel/actionpanel.selectors';
import { BillingDataSelectors } from 'reactApp/modules/billingData/billingData.selectors';
import { getSlicedHome } from 'reactApp/modules/file/utils';
import { getIsFastscrollEnabled } from 'reactApp/modules/gallery/gallery.selectors';
import { changeHomeHistory, loadHomeFolderRequest } from 'reactApp/modules/home/home.actions';
import { getHomeFoldersTree } from 'reactApp/modules/home/home.selectors';
import { getIntegrationClient } from 'reactApp/modules/integration/integration.selectors';
import { openPopupHelper } from 'reactApp/modules/popup/popup.helpers';
import { popupNames } from 'reactApp/modules/popup/popup.types';
import { storeHelper } from 'reactApp/modules/promo/promo.helpers';
import { historyPush } from 'reactApp/modules/router/router.module';
import { getCurrentStorage, selectStatusPage } from 'reactApp/modules/router/router.selectors';
import { sidebarToggle } from 'reactApp/modules/settings/settings.module';
import { SettingsSelectors } from 'reactApp/modules/settings/settings.selectors';
import { getIdByStorage, getStorage } from 'reactApp/modules/storage/storage.helpers';
import { getIdsCount, isRootCurrentFolder } from 'reactApp/modules/storage/storage.selectors';
import { EStorageType } from 'reactApp/modules/storage/storage.types';
import { isCurrentDarkTheme } from 'reactApp/modules/theme/theme.selectors';
import { getAllowedMimeTypes, getIsDragging } from 'reactApp/modules/upload/upload.selectors';
import { addToUploadQueue } from 'reactApp/modules/uploading/uploading.module';
import { UserSelectors } from 'reactApp/modules/user/user.selectors';
import { getVigboLinkState, hasActiveVigboTariff } from 'reactApp/modules/vigbo/vigbo.selectors';
import { VigboAccountLinkState } from 'reactApp/modules/vigbo/vigbo.types';
import { ErrorPage } from 'reactApp/sections/ErrorPage/ErrorPage';
import { useBanner } from 'reactApp/sections/MainPage/hooks/useBanner';
import { useCommonInformers } from 'reactApp/sections/MainPage/hooks/useCommonInformers';
import { useFreemiumBanners } from 'reactApp/sections/MainPage/hooks/useFreemiumBanners';
import { useFreemiumModals } from 'reactApp/sections/MainPage/hooks/useFreemiumModals';
import { useHomeInformers } from 'reactApp/sections/MainPage/hooks/useHomeInformers';
import { useMobileAppPromo } from 'reactApp/sections/MainPage/hooks/useMobileAppPromo';
import { useRouteChangeProcessing } from 'reactApp/sections/MainPage/hooks/useRouteChangeProcessing';
import { useTelegramMigrationPromo } from 'reactApp/sections/MainPage/hooks/useTelegramMigrationPromo';
import { useVKTechBanner } from 'reactApp/sections/MainPage/hooks/useVKTechBanner';
import MainPageDisableWrapper from 'reactApp/sections/MainPage/MainPageDisableWrapper/MainPageDisableWrapper';
import { WorkspaceSuperAppService } from 'reactApp/services';
import type { RootState } from 'reactApp/store';
import { BlockedB2BOverquota } from 'reactApp/ui/BlockedB2BOverquota/BlockedB2BOverquota';
import { CloudFilesMigrationPromo } from 'reactApp/ui/CloudFilesMigrationPromo/CloudFilesMigrationPromo';
import { isMobile } from 'reactApp/ui/Datalist/DataList.constants';
import { FamilyAddButton } from 'reactApp/ui/FamilyAddButton/FamilyAddButton';
import FreemiumOnboardBannerWrapper from 'reactApp/ui/FreemiumOnboardBanner/FreemiumOnboardBanner';
import { EHeaderColor } from 'reactApp/ui/Header/HeaderWrapper';
import { NeuroPromoObject } from 'reactApp/ui/NeuroPromoObject/NeuroPromoObject';
import { NotifyContainer } from 'reactApp/ui/NotifyContainer/NotifyContainer';
import { OnboardingPanel } from 'reactApp/ui/OnboardingPanel/OnboardingPanel';
import { TreePromoDesktopApp } from 'reactApp/ui/ReactViewer/TreePromoMobileApp/TreePromoDesktopApp';
import { SAFE_FAKEDOOR_STORE_ID } from 'reactApp/ui/SafeFakedoorModal/SafeFakedoorModal.constants';
import { SidebarBanner } from 'reactApp/ui/SidebarBanner/SidebarBanner';
import { Space } from 'reactApp/ui/Space/Space';
import { AddToolbar } from 'reactApp/ui/Toolbar/AddToolbar';
import { SuperAppToolbar } from 'reactApp/ui/Toolbar/SuperAppToolbar';
import { ToolbarNew } from 'reactApp/ui/Toolbar/Toolbar.new';
import { ToolbarItem } from 'reactApp/ui/Toolbar/ToolbarItem/ToolbarItem';
import { TreeConnected } from 'reactApp/ui/TreeComponent/TreeComponent';
import { ETreeRootIds } from 'reactApp/ui/TreeComponent/TreeComponent.constants';
import { loadFolder } from 'reactApp/ui/TreeComponent/TreeComponent.helpers';
import { TreePromo } from 'reactApp/ui/TreePromo/TreePromo';
import { UploadBlock } from 'reactApp/ui/UploadBlock/UploadBlock';
import { UploadInput } from 'reactApp/ui/UploadInput/UploadInput';
import { ChevronLeftOutlineIcon } from 'reactApp/ui/VKUIIcons';
import { usePHSearchBlock } from 'reactApp/ui/WebSearch/helpers';
import { useShowAd } from 'reactApp/ui/Worm/useShowAd';
import { Worm } from 'reactApp/ui/Worm/Worm';
import { changeCloudToDocumentDomain } from 'reactApp/utils/documentsDomain';
import { createGaSender, sendDwh, sendGa as sendGaHelper } from 'reactApp/utils/ga';
import { filterFiles, scrollToTop } from 'reactApp/utils/helpers';
import opener from 'reactApp/utils/opener';
import { ECategoryGa, sendPaymentGa } from 'reactApp/utils/paymentGa';
import { ESafeFakedoorAnalytics, safeFakedoorAnalytics } from 'reactApp/utils/safeFakedoorGa';

import { useDiskOTreePromo } from './hooks/useDiskOTreePromo';
import { useLeftBlockTopPromo } from './hooks/useLeftBlockPromo';
import { SidebarAdSize, useSidebarAdSize } from './hooks/useSidebarAdSize';
import styles from './MainPage.css';

const sendGa = createGaSender('main');

interface Props {
    children: ReactElement | null;
    storage?: EStorageType;
    showUploader?: boolean;
    allowDnd?: boolean;
    renderWorm?: boolean;
    showToolbar?: boolean;
    showSpace?: boolean;
    showTree?: boolean;
    showBackHomeButton?: boolean;
    disabled?: boolean;
    disableContent?: ReactElement;
    disableUpload?: boolean;
}

export const MainPage = memo(
    // eslint-disable-next-line max-lines-per-function, complexity
    ({
        children,
        storage,
        showUploader = true,
        allowDnd = true,
        renderWorm = false,
        showToolbar = true,
        showSpace = true,
        showTree = true,
        showBackHomeButton = false,
        disabled = false,
        disableContent,
        disableUpload = false,
    }: Props): ReactElement | null => {
        const dispatch = useDispatch();
        const location = useLocation();
        const params = useParams();
        const [largeLBreakpointHit] = useMinWidthBreakpoint(BREAKPOINT_MD);
        const isResponsiveEnable = useResponsiveEnabled();

        const isDarkTheme = useSelector(isCurrentDarkTheme);
        const isNewbie = useSelector(UserSelectors.isNewbie);
        const isAnonymous = useSelector(UserSelectors.isAnonymous);
        const isFrozen = useSelector(UserSelectors.isFrozen);
        const statusPage = useSelector(selectStatusPage);
        const isSidebarOpened = useSelector(SettingsSelectors.isSidebarOpened);
        const allowedMimeTypes = useSelector(getAllowedMimeTypes)?.join(',');
        const isActionPanelOpened = useSelector(ActionPanelSelectors.isActionPanelOpened);
        const currentStorage = useSelector(getCurrentStorage);
        const isDragging = useSelector(getIsDragging);
        const isFreemium = useSelector(BillingDataSelectors.getFreemiumSubscription);

        const uploadPortalTarget = usePortal('uploadAreaRoot');
        const helpButtonPortalTarget = usePortal('helpButtonRoot');
        const onboardingPortalTarget = usePortal('onboardingPortalTarget');

        const leftBlockWrapperRef = useRef<HTMLDivElement | null>(null);

        const { showDiskOTreePromo, handleDiskOTreePromoShow, handleDiskOTreePromoClick, handleDiskOTreePromoClose } = useDiskOTreePromo();
        const { showMobileAppTreePromo, handleTreePromoMobileAppClick } = useMobileAppPromo();
        const { showTelegramMigrationPromo, handleTelegramMigrationClick } = useTelegramMigrationPromo();
        const { showVKTechBanner } = useVKTechBanner();

        const {
            isHome,
            isAttaches,
            isIntegration,
            isAllDocuments,
            isIncomingPublic,
            isDocuments,
            isInlineIntegration,
            isFamilyPage,
            isTemplates,
            isFamilyFolders,
            isFamilyAlbums,
            isVigbo,
            isGallery,
        } = getStorage(storage as EStorageType);

        const homeInformer = useHomeInformers(isHome);
        const commonInformer = useCommonInformers();

        const isFastScrollEnabled = useSelector(getIsFastscrollEnabled);
        const withoutScrollbar = isGallery && !isMobile && isFastScrollEnabled;

        const { isTutoria } = useSelector(getIntegrationClient);
        const isTutoriaIntegration = isIntegration && isTutoria;
        const windowWidthSmall = useIsWindowWidthSmall();

        const overquotaBanner = useBanner();
        const freemiumBanner = useFreemiumBanners();
        useFreemiumModals();
        usePersonalizationInit();

        const closeSidebarHandler = useCallback(() => dispatch(sidebarToggle(false)), [dispatch]);

        usePHSearchBlock();

        useEffect(() => {
            closeSidebarHandler();
        }, [largeLBreakpointHit, closeSidebarHandler]);

        const homeFolderRequestRef = useRef(false);
        const homeFoldersTree = useSelector(getHomeFoldersTree);
        useEffect(() => {
            if (!homeFolderRequestRef.current && !homeFoldersTree.length && showTree && aiPhotoGenerationEnabled) {
                homeFolderRequestRef.current = true;
                dispatch(loadHomeFolderRequest({ id: '/', isFolder: true }));
            }
        }, [homeFoldersTree.length, showTree, dispatch]);

        const handleMenuClick = useCallback(
            ({ id, storage, level }) => {
                const domainBizFoldersSection = id === ETreeRootIds.domain && level === 0;
                const isAllDocuments = id === ETreeRootIds.alldocuments;
                const isBoard = id === ETreeRootIds.board;

                if (isAllDocuments) {
                    sendPaymentGa({ eventCategory: ECategoryGa.entered, action: 'docs-from-menu' });
                    const path = `${changeCloudToDocumentDomain(window.location.origin)}?fromPage=${currentStorage}`;
                    opener(path);
                    return;
                }

                if (isBoard) {
                    sendDwh({ eventCategory: 'menu-board-click' });
                    opener(boardUrl);
                    return;
                }

                if (domainBizFoldersSection) {
                    return;
                }

                if (
                    chooseVariant(
                        abSafeFakeDoorSelector,
                        { control: false, variant2: id === ETreeRootIds.safe },
                        { skipCondition: { variant1: true } }
                    )
                ) {
                    safeFakedoorAnalytics(ESafeFakedoorAnalytics.CLICK, { entity: 'chapter' });

                    openPopupHelper({
                        popupName: popupNames.SAFE_FAKEDOOR,
                    });

                    return;
                }

                const { isHome, isIntegration } = getStorage(storage);

                if (isHome || isIntegration || isInlineIntegration) {
                    // для storage === integration || inline-integration критически важно не терять гет параметры при навигации
                    dispatch(changeHomeHistory({ id: getSlicedHome(id), search: location.search }));
                } else {
                    dispatch(historyPush({ id }));
                }
            },
            [location, currentStorage]
        );

        const handleOnMenuExpand = useCallback(({ id, storage, dispatch }) => {
            loadFolder({ id, storage, dispatch });
        }, []);

        useEffect(() => {
            scrollToTop();

            sendGa('show');
            sendGaHelper('lm', 'show');
        }, []);

        useRouteChangeProcessing({ storage, path: location.pathname, search: location.search });

        useEffect(() => {
            if (
                !isHome &&
                !isIntegration &&
                !isInlineIntegration &&
                !isAttaches &&
                !isAllDocuments &&
                !isIncomingPublic &&
                (isAnonymous || isFrozen || isNewbie) &&
                !IS_DOCUMENTS_DOMAIN
            ) {
                open(isAnonymous ? '/promo' : '/home', '_self');
            }
        }, [isFrozen, isAnonymous, storage, isHome, isAttaches, isIntegration, isAllDocuments, isIncomingPublic, isNewbie]);

        useEffect(() => {
            const classes = classNames(styles.container, {
                [styles.responsive]: ENABLE_FULL_RESPONSIVE,
            }).split(' ');
            uploadPortalTarget.classList.remove(...classes);
            if (isActionPanelOpened) {
                uploadPortalTarget.classList.add(...classes);
            }
        }, [uploadPortalTarget, isActionPanelOpened]);

        useEffect(() => {
            const uploadFilesFromClipboard = async (e: ClipboardEvent) => {
                if (!e.clipboardData?.files.length) {
                    return;
                }

                const files = filterFiles(Array.from(e.clipboardData.files));

                if (!files.length) {
                    return;
                }

                e.preventDefault();

                dispatch(
                    addToUploadQueue({
                        files,
                        descriptorOptions: {
                            checkAllowedExtensions: true,
                        },
                    })
                );
            };

            document.addEventListener('paste', uploadFilesFromClipboard);
            return () => document.removeEventListener('paste', uploadFilesFromClipboard);
        }, [currentStorage]);

        const showAD = useShowAd();
        const adSidebarSize = useSidebarAdSize();

        const isAdSidebarCollapsed = adSidebarSize === SidebarAdSize.None;
        const isAdSidebarSmall = adSidebarSize === SidebarAdSize.Small;
        const isAdSidebarBig = adSidebarSize === SidebarAdSize.Big;
        const isWorkspaceSuperappEnabled = WorkspaceSuperAppService.isEnabled;
        const isUnifiedSearchToolbar =
            (isWorkspaceSuperappEnabled || UNIFIED_SEARCH_IN_SUPERAPP_ENABLED) && !WorkspaceSuperAppService.isB2CMode;

        const isAdWorm = renderWorm && showAD && !isHomeSidebar;
        const isAdSidebar = renderWorm && showAD && isHomeSidebar;

        const isSpaceAtTopPosition = showSpace && !IS_SPACE_POSITION_BOTTOM;
        const isSpaceAtBottomPosition = showSpace && IS_SPACE_POSITION_BOTTOM;

        const [uploadButtonOffset, setUploadButtonOffset] = useState<number | undefined>();
        const ref = useRef<HTMLDivElement>(null);

        useEffect(() => {
            let styles;
            if (ref?.current) {
                styles = window.getComputedStyle(ref.current);
            }

            if (isAdSidebar && styles?.display !== 'none') {
                if (isAdSidebarSmall) {
                    setUploadButtonOffset(300);
                } else if (isAdSidebarBig) {
                    setUploadButtonOffset(380);
                }
            } else {
                setUploadButtonOffset(undefined);
            }
        }, [ref, isAdSidebar, isAdSidebarSmall, isAdSidebarBig]);

        const showLeftBlock = () => {
            if (isTutoriaIntegration) {
                return !IS_BLOCKED && !windowWidthSmall;
            }

            return !IS_BLOCKED && !isInlineIntegration;
        };

        const { leftBlockTopPromo, isLeftBlockTopPromoVisible } = useLeftBlockTopPromo();

        const spaceBlock = (
            <Space
                className={classNames({
                    [styles.spacePromo_new_portal_header]: NEW_PORTAL_HEADER,
                    [styles.spacePromo_with_unified_search_in_superapp]: isUnifiedSearchToolbar,
                })}
            />
        );

        const leftBlock = useMemo(
            () => (
                <div
                    className={classNames(styles.leftBlock, {
                        [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                        [styles.left_block_with_unified_search_in_superapp]: isUnifiedSearchToolbar,
                    })}
                    data-qa-id="main-page-left-block"
                >
                    {showToolbar && (
                        <div
                            className={classNames(styles.toolbar, {
                                [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                                [styles.toolbar_new_portal_header]: NEW_PORTAL_HEADER,
                                [styles.toolbar_with_unified_search_in_superapp]: isUnifiedSearchToolbar,
                                [styles.toolbar_with_space_at_bottom]: isSpaceAtBottomPosition,
                            })}
                        >
                            {ENABLE_FULL_RESPONSIVE && (
                                <ToolbarItem
                                    id="toolbar-collapse-item"
                                    text="Свернуть"
                                    icon={<ChevronLeftOutlineIcon />}
                                    onClick={closeSidebarHandler}
                                />
                            )}
                            {isUnifiedSearchToolbar ? (
                                <SuperAppToolbar disabled={disabled} disableSearch={!isWorkspaceSuperappEnabled} />
                            ) : (
                                <AddToolbar disabled={disabled} />
                            )}
                        </div>
                    )}
                    {showBackHomeButton && (
                        <div className={styles.backButtonBlock}>
                            <RouterLink to={getIdByStorage(EStorageType.home)} reloadDocument>
                                <ToolbarItem className={styles.backButton} id="back" text="Вернуться" icon={<ChevronLeftOutlineIcon />} />
                            </RouterLink>
                        </div>
                    )}

                    {isSpaceAtTopPosition && spaceBlock}

                    {showTelegramMigrationPromo && (
                        <CloudFilesMigrationPromo
                            className={classNames({
                                [styles.cloudFilesMigrationPromo_with_unified_search_in_superapp]: isUnifiedSearchToolbar,
                            })}
                            onClick={handleTelegramMigrationClick}
                        />
                    )}

                    {familyAddButton && <FamilyAddButton />}

                    {leftBlockTopPromo && isLeftBlockTopPromoVisible ? (
                        <>
                            {leftBlockTopPromo}
                            <Spacing size={20} />
                        </>
                    ) : (
                        <Spacing size={12} />
                    )}

                    {showDiskOTreePromo && DOWNLOAD_DISK_O_PROMO_CONFIG?.treePromoLink && (
                        <TreePromo
                            text="Скачать Облако для ПК"
                            icon={<Icon24DownloadOutline />}
                            href={DOWNLOAD_DISK_O_PROMO_CONFIG?.treePromoLink}
                            onShow={handleDiskOTreePromoShow}
                            onClick={handleDiskOTreePromoClick}
                            onClose={handleDiskOTreePromoClose}
                        />
                    )}
                    {/* если не отображаем ничего из вышеперечисленного, то хотя бы добавим отступ */}
                    {!showToolbar && !showSpace && !showDiskOTreePromo && <Spacing size={24} />}
                    {showTree && (
                        <div
                            className={classNames(styles.treeWrapper, {
                                [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                                [styles.treeWrapper_new_portal_header]: NEW_PORTAL_HEADER,
                            })}
                        >
                            <TreeConnected
                                key={storeHelper.getValue(SAFE_FAKEDOOR_STORE_ID)}
                                onClick={handleMenuClick}
                                onExpand={handleOnMenuExpand}
                                gaSuffix="sp"
                                search={location?.search}
                            />
                        </div>
                    )}
                    {showMobileAppTreePromo && <TreePromoDesktopApp onClick={handleTreePromoMobileAppClick} />}
                </div>
            ),
            [
                location?.search,
                storage,
                handleMenuClick,
                showDiskOTreePromo,
                handleDiskOTreePromoShow,
                handleDiskOTreePromoClick,
                handleDiskOTreePromoClose,
                closeSidebarHandler,
                storeHelper.getValue(SAFE_FAKEDOOR_STORE_ID),
                leftBlockTopPromo,
                isLeftBlockTopPromoVisible,
                isSpaceAtTopPosition,
                isSpaceAtBottomPosition,
            ]
        );

        const handleLeftBlockWrapperOverlayClick = useCallback(
            (evt) => {
                if (ENABLE_FULL_RESPONSIVE && leftBlockWrapperRef.current === evt.target) {
                    closeSidebarHandler();
                }
            },
            [closeSidebarHandler]
        );

        const itemsCount = useSelector((state: RootState) => getIdsCount(state, storage as EStorageType));
        const isRootFolder = useSelector((state: RootState) => isRootCurrentFolder(state, storage));
        const isB2BBizUser = useSelector(UserSelectors.isB2BUser);

        const isVigboActiveTariff = useSelector(hasActiveVigboTariff);
        const vigboLinkState = useSelector(getVigboLinkState);
        const hideToolbarInVigbo =
            isVigbo && !isVigboActiveTariff && !forceVigboLinkedAccount && vigboLinkState !== VigboAccountLinkState.linked;

        const hideToolbar =
            isFamilyFolders ||
            isFamilyAlbums ||
            (itemsCount === 0 && (isHome || isFamilyPage) && isRootFolder && !isB2BBizUser) ||
            (isDocuments && !isResponsiveEnable && !params.documentType) ||
            isInlineIntegration ||
            isTemplates ||
            hideToolbarInVigbo;

        if (isFrozen) {
            return null;
        }

        if (statusPage) {
            return <ErrorPage status={statusPage} />;
        }

        if (BLOCKED_B2B_OVERQUOTA && !SHOW_BLOCKED) {
            return <BlockedB2BOverquota />;
        }

        return (
            <>
                <div
                    className={classNames(styles.root, {
                        [styles.root_new_portal_header]: NEW_PORTAL_HEADER,
                        [styles.alignLeft]: isInlineIntegration,
                        // tempexp-17235-start
                        [styles.root_sidebar_banner]: isAdSidebar && isAdSidebarBig,
                        [styles.root_sidebar_banner__small]: isAdSidebar && isAdSidebarSmall,
                        // tempexp-17235-end
                        [styles.root_integration]: isTutoriaIntegration,
                        [styles.root_new_portal_header_rightBlock_notFullHeight]: isFamilyPage,
                        [styles.root_with_unified_search_in_superapp]: isUnifiedSearchToolbar,
                        [styles.root_uploader]: isDragging,
                    })}
                >
                    {showLeftBlock() && (
                        <div
                            ref={leftBlockWrapperRef}
                            onClick={handleLeftBlockWrapperOverlayClick}
                            className={classNames(styles.leftBlockWrapper, {
                                [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                                [styles.visible]: isSidebarOpened,
                            })}
                        >
                            {leftBlock}
                            {showVKTechBanner && (
                                <div className={styles.vktech} data-qa-id="vktech-banner">
                                    <Spacing size={12} />
                                    {isSpaceAtBottomPosition && spaceBlock}
                                    <a href="//tech.vk.ru" className={styles.vktech_link} target="_blank" rel="noreferrer">
                                        <img src={isDarkTheme ? vkTechDarkImg : vkTechLightImg} alt="Логотип VKTech" />
                                    </a>
                                </div>
                            )}
                        </div>
                    )}
                    {!IS_BLOCKED && isHome && createPortal(<OnboardingPanel />, onboardingPortalTarget)}
                    {!IS_BLOCKED && (
                        <div
                            className={classNames(styles.rightBlock, {
                                [styles.responsive]: ENABLE_FULL_RESPONSIVE,
                                [styles.rightBlock_new_portal_header]: NEW_PORTAL_HEADER || isTutoriaIntegration,
                                [styles.rightBlock_with_unified_search_in_superapp]: isUnifiedSearchToolbar,
                                [styles.disabled]: disabled,
                                [styles.rightBlockWithoutScroll]: withoutScrollbar,
                            })}
                        >
                            {freemiumBanner || overquotaBanner}
                            {/* Нельзя менять id / удалять div снизу - в него рендерятся промо-баннеры */}
                            {!freemiumBanner && !overquotaBanner && <div id="banners-above-toolbar" />}
                            <MainPageDisableWrapper
                                className={styles.rightBlockPadding}
                                disabled={disabled}
                                disableContent={disableContent}
                            >
                                <div className={styles.rightBlockContent}>
                                    {homeInformer}
                                    {isAdWorm && (
                                        <div className={styles.wormWrapper}>
                                            <Worm />
                                        </div>
                                    )}
                                    {NEW_PORTAL_HEADER && !hideToolbar && !(isTutoriaIntegration && windowWidthSmall) && (
                                        <div className={styles.toolbarWrap}>
                                            <ToolbarNew color={EHeaderColor.WHITE} isResponsiveEnable={isResponsiveEnable} />
                                        </div>
                                    )}
                                    {commonInformer}
                                    {NEW_PORTAL_HEADER || isTutoriaIntegration ? (
                                        <div
                                            className={classNames(styles.rightBlockContentWrapper, {
                                                [styles.rightBlockContentWrapperDisabled]: disabled,
                                            })}
                                        >
                                            {children}
                                        </div>
                                    ) : (
                                        children
                                    )}
                                </div>
                            </MainPageDisableWrapper>
                        </div>
                    )}
                    {!IS_BLOCKED && isAdSidebar && !isAdSidebarCollapsed && <SidebarBanner isSmall={isAdSidebarSmall} ref={ref} />}

                    {!IS_BLOCKED &&
                        !disableUpload &&
                        createPortal(
                            <UploadBlock
                                showDropArea={showUploader}
                                allowDnd={allowDnd}
                                gaCategory="fast"
                                // tempexp-17235-start
                                rightOffset={uploadButtonOffset}
                                // tempexp-17235-end
                            />,
                            uploadPortalTarget
                        )}

                    {(isAllDocuments || isIncomingPublic) &&
                        isAllDocumentsHelpButton &&
                        createPortal(<HelpButton storage={storage} />, helpButtonPortalTarget)}

                    <UploadInput allowedMimeTypes={allowedMimeTypes} />
                    {isNeuroTariffPromoObject && <NeuroPromoObject />}
                    {isFreemiumOnboardBannerEnable && !isFreemium && <FreemiumOnboardBannerWrapper />}
                </div>
                <NotifyContainer />
            </>
        );
    }
);

MainPage.displayName = 'MainPage';

MainPage.whyDidYouRender = true;
